/**
 * Local CodeStitch image library adapter. Searches the image-index.json catalog
 * of logical images in-memory with category + text filtering. No API key required.
 *
 * Some hosts block browser fetch() by CORS even when <img> renders fine. In that case,
 * the picker can fall back to applying the original URL directly.
 */

import { loadJSON, loadAsset, getAssetURLSync } from '../../core/asset-loader.js';
import { loadAssetLivenessData, isKnownDeadAssetUrl } from '../../shared/asset-liveness-data.js';

const PER_PAGE = 60;
const DOWNLOAD_TIMEOUT_MS = 15_000;
const IMAGE_EXTS = new Set(['.jpg', '.jpeg', '.png', '.webp', '.gif', '.avif', '.svg']);
const VIDEO_EXTS = new Set(['.mp4', '.webm', '.mov', '.m4v', '.avi']);
const SEARCH_FIELD_SEPARATOR = '\u0000';

let indexCache = null;
let sortedIndexCache = null;
let urlAliasCache = null;
let categoriesCache = null;
const corsDeniedOrigins = new Set();

function normalizeUrl(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  let url = rawUrl.trim();
  if (!url) return '';
  url = url.replace(/&amp;/gi, '&');
  if (url.startsWith('//')) {
    url = `https:${url}`;
  }
  if (!/^(https?:|data:|blob:)/i.test(url)) return '';
  try {
    return new URL(url).toString();
  } catch {
    return '';
  }
}

function basenameFromUrl(rawUrl) {
  const normalized = normalizeUrl(rawUrl);
  if (normalized) {
    try {
      return decodeURIComponent(new URL(normalized).pathname.split('/').pop() || '');
    } catch {
      return '';
    }
  }
  return String(rawUrl || '')
    .split(/[?#]/)[0]
    .split('/')
    .pop()
    .trim();
}

function getExtFromPath(pathname = '') {
  const idx = pathname.lastIndexOf('.');
  if (idx < 0) return '';
  return pathname.slice(idx).toLowerCase();
}

function filenameFromUrl(rawUrl) {
  const basename = basenameFromUrl(rawUrl);
  return basename || 'Image';
}

function formatFromUrl(rawUrl) {
  const filename = filenameFromUrl(rawUrl);
  return getExtFromPath(filename).replace(/^\./, '') || 'unknown';
}

function legacyAssetKey(rawUrl) {
  const normalized = normalizeUrl(rawUrl);
  if (!normalized) return '';
  try {
    const url = new URL(normalized);
    const parts = url.pathname.split('/');
    const filename = parts.pop() || '';
    const extension = getExtFromPath(filename);
    let base = extension ? filename.slice(0, -extension.length) : filename;
    if (url.pathname.includes('/_astro/')) {
      base = base.replace(/_[A-Za-z0-9-]{4,}$/i, '');
    }
    base = base
      .replace(/-\d+w$/i, '')
      .replace(/@[\d.]+x$/i, '')
      .replace(/-[a-f0-9]{8,}$/i, '');
    return `${url.origin}${parts.join('/')}/${base}`;
  } catch {
    return normalized;
  }
}

function formatRank(format) {
  const value = String(format || '')
    .toLowerCase()
    .replace(/^\./, '');
  if (value === 'jpg' || value === 'jpeg' || value === 'png') return 0;
  if (value === 'webp') return 1;
  if (value === 'avif') return 2;
  return 3;
}

function normalizeVariant(variant, fallbackUrl = '') {
  const source = typeof variant === 'string' ? { url: variant } : variant || {};
  const normalizedUrl = normalizeUrl(source.url || fallbackUrl);
  if (!normalizedUrl) return null;
  const filename = source.filename || filenameFromUrl(normalizedUrl);
  const rawWidth = Number(source.width);
  return {
    url: source.url || fallbackUrl,
    _normalizedUrl: normalizedUrl,
    filename,
    format: source.format || formatFromUrl(normalizedUrl),
    descriptor: source.descriptor || '',
    width: Number.isFinite(rawWidth) && rawWidth > 0 ? rawWidth : null,
    // Carried through explicitly: this shape is rebuilt field by field, so a
    // key the index stamped is dropped unless it is named here. Each variant
    // keeps its OWN key — they are different files.
    vendoredKey: typeof source.vendoredKey === 'string' ? source.vendoredKey : ''
  };
}

function chooseCanonicalVariant(variants) {
  return (
    [...variants].sort((a, b) => {
      const rankDiff = formatRank(a.format) - formatRank(b.format);
      if (rankDiff !== 0) return rankDiff;
      return (b.width || 0) - (a.width || 0);
    })[0] || null
  );
}

function chooseThumbVariant(variants, canonical) {
  const withWidth = variants.filter((v) => v.width);
  const preferred = withWidth
    .filter((v) => v.width >= 320)
    .sort((a, b) => a.width - b.width || formatRank(a.format) - formatRank(b.format))[0];
  if (preferred) return preferred;
  return withWidth.sort((a, b) => a.width - b.width)[0] || canonical;
}

function addVariantToGroup(group, variant) {
  if (!variant?._normalizedUrl) return;
  const existing = group.variantsByUrl.get(variant._normalizedUrl);
  if (!existing) {
    group.variantsByUrl.set(variant._normalizedUrl, { ...variant });
    return;
  }
  if (!existing.descriptor && variant.descriptor) existing.descriptor = variant.descriptor;
  if (!existing.width && variant.width) existing.width = variant.width;
  if (!existing.vendoredKey && variant.vendoredKey) existing.vendoredKey = variant.vendoredKey;
}

function buildNormalizedEntries(rawIndex) {
  const groups = new Map();

  for (const entry of rawIndex || []) {
    const normalizedUrl = normalizeUrl(entry?.url);
    if (!normalizedUrl) continue;
    if (!isImageLikeEntry(entry, normalizedUrl)) continue;

    const assetKey = entry.assetKey || legacyAssetKey(normalizedUrl);
    if (!assetKey) continue;
    const groupKey = `${String(entry.sourceDomain || '').toLowerCase()}\u0000${assetKey}`;
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        assetKey,
        entries: [],
        variantsByUrl: new Map(),
        categories: new Set()
      });
    }

    const group = groups.get(groupKey);
    group.entries.push({ ...entry, _normalizedUrl: normalizedUrl });
    if (entry.sectionCategory) group.categories.add(entry.sectionCategory);

    const variants =
      Array.isArray(entry.variants) && entry.variants.length > 0
        ? entry.variants
        : [{ url: entry.url, filename: entry.filename, format: entry.format }];

    for (const variant of variants) {
      const normalizedVariant = normalizeVariant(variant, normalizedUrl);
      if (normalizedVariant) addVariantToGroup(group, normalizedVariant);
    }

    const thumbVariant = normalizeVariant(entry.thumbUrl);
    if (thumbVariant) addVariantToGroup(group, thumbVariant);
  }

  const normalized = [];
  const aliasMap = new Map();

  for (const group of groups.values()) {
    const variants = [...group.variantsByUrl.values()].sort(
      (a, b) =>
        (a.width || Number.MAX_SAFE_INTEGER) - (b.width || Number.MAX_SAFE_INTEGER) ||
        formatRank(a.format) - formatRank(b.format) ||
        a.filename.localeCompare(b.filename)
    );
    const firstEntry = group.entries[0];
    const hasGroupedShape = Array.isArray(firstEntry?.variants) && firstEntry.variants.length > 0;
    const canonicalUrl = normalizeUrl(firstEntry?.url);
    const canonical =
      variants.find((v) => v._normalizedUrl === canonicalUrl) || chooseCanonicalVariant(variants);
    if (!canonical) continue;
    const explicitThumb = normalizeUrl(firstEntry?.thumbUrl);
    const thumb =
      variants.find((v) => v._normalizedUrl === explicitThumb) ||
      chooseThumbVariant(variants, canonical);
    const usageCount = Math.max(...group.entries.map((entry) => Number(entry.usageCount) || 0), 1);

    const entry = {
      ...firstEntry,
      assetKey: group.assetKey,
      url: canonical.url,
      filename: hasGroupedShape ? firstEntry?.filename || canonical.filename : canonical.filename,
      format: hasGroupedShape ? firstEntry?.format || canonical.format : canonical.format,
      thumbUrl: thumb?._normalizedUrl || canonical._normalizedUrl,
      variantCount: Number(firstEntry?.variantCount) || variants.length,
      variants: variants.map(({ _normalizedUrl, ...variant }) => variant),
      _normalizedUrl: canonical._normalizedUrl,
      _thumbUrl: thumb?._normalizedUrl || canonical._normalizedUrl,
      _variants: variants,
      _variantCount: Number(firstEntry?.variantCount) || variants.length,
      _sectionCategories: group.categories,
      usageCount,
      // MK-3. Judged on the CANONICAL url, not on any variant, because that is
      // the one `getSourceUrl` returns and therefore the one `download()`
      // fetches. `isKnownDeadAssetUrl` is an exact string match after the
      // module's own www/bare host expansion, so a variant may be dead while
      // this is not — and the reverse. Only the url we would actually request
      // decides whether the pick can succeed.
      _dead:
        isKnownDeadAssetUrl(canonical._normalizedUrl, firstEntry?.sourceDomain) &&
        !canonical.vendoredKey,
      // A VENDORED BYTE WINS here too. The index stamps a key per canonical,
      // thumbnail and variant because they are DIFFERENT urls — the thumbnail is
      // the smallest >=320w variant while the download is the canonical, so one
      // key would leave the other hotlinked.
      _vendoredKey: canonical.vendoredKey || '',
      _thumbVendoredKey: thumb?.vendoredKey || canonical.vendoredKey || ''
    };

    // Precompute the lowercased search haystack ONCE (built at index load, not
    // per keystroke over the full ~5k-entry set). Fields are separated by a
    // sentinel so substring matches stay field-local, preserving the old
    // multi-field OR semantics.
    entry._haystack = [
      entry.filename,
      entry.extractedFrom,
      entry.sectionCategory,
      entry.assetKey,
      ...variants.map((v) => v.filename)
    ]
      .filter(Boolean)
      .join(SEARCH_FIELD_SEPARATOR)
      .toLowerCase();

    normalized.push(entry);
    for (const variant of variants) {
      aliasMap.set(variant._normalizedUrl, entry);
    }
    aliasMap.set(entry._normalizedUrl, entry);
  }

  return {
    entries: normalized.sort((a, b) => (b.usageCount || 0) - (a.usageCount || 0)),
    aliasMap
  };
}

function isImageLikeEntry(entry, normalizedUrl) {
  let ext = '';
  try {
    ext = getExtFromPath(new URL(normalizedUrl).pathname);
  } catch {
    ext = '';
  }

  const filenameExt = getExtFromPath(String(entry?.filename || ''));
  const formatExt = String(entry?.format || '')
    .trim()
    .toLowerCase();
  const normalizedFormatExt = formatExt ? `.${formatExt.replace(/^\./, '')}` : '';
  const candidate = ext || filenameExt || normalizedFormatExt;

  if (VIDEO_EXTS.has(candidate)) return false;
  if (IMAGE_EXTS.has(candidate)) return true;
  return false;
}

/**
 * The url the picker should DISPLAY for this entry's thumbnail: the vendored
 * copy when the index stamped one, else the origin. Display and download are
 * resolved separately on purpose — they are different urls.
 */
function thumbDisplayUrl(entry) {
  if (entry?._thumbVendoredKey) return getAssetURLSync(entry._thumbVendoredKey);
  return entry?._thumbUrl || entry?._normalizedUrl || '';
}

async function loadIndex() {
  if (indexCache) return indexCache;
  // MK-3: the liveness facts land BEFORE the entries are built, because the
  // dead flag is stamped during the build. `loadAssetLivenessData` is the ONE
  // reader — never a second parse of data/asset-liveness.json — and it is
  // fail-soft by construction: a missing or unloaded file yields an empty set
  // and nothing is marked dead. That is the contract, not a bug, and it is why
  // this surface may say "known unavailable" and must never say "available".
  const [index] = await Promise.all([
    loadJSON('data/image-index.json'),
    loadAssetLivenessData(loadJSON)
  ]);
  indexCache = index;
  const normalized = buildNormalizedEntries(indexCache);
  // Keep an immutable usage-sorted view for fast filtered scans without re-sorting each search.
  sortedIndexCache = normalized.entries;
  urlAliasCache = normalized.aliasMap;
  return indexCache;
}

/**
 * Drop every memoized view of the index (tests only).
 *
 * The dead flag is stamped during `buildNormalizedEntries`, so seeding liveness
 * data after the index has been built changes nothing — a test that wants a
 * different liveness answer must rebuild. Mirrors `_resetCachesForTesting` in
 * `editing/image-editor.js`.
 */
export function _resetLocalImageCachesForTesting() {
  indexCache = null;
  sortedIndexCache = null;
  urlAliasCache = null;
  categoriesCache = null;
  corsDeniedOrigins.clear();
}

/**
 * Get unique sectionCategory values from the index.
 */
export async function getCategories() {
  if (categoriesCache) return categoriesCache;
  await loadIndex();
  const index = sortedIndexCache || [];
  const cats = new Set();
  for (const entry of index) {
    if (entry.sectionCategory) cats.add(entry.sectionCategory);
  }
  categoriesCache = [...cats].sort();
  return categoriesCache;
}

export const localImages = {
  name: 'Local',
  needsKey: false,
  lsKey: null,
  attribution: 'Images from CodeStitch template library',
  keyHelp: '',

  // This tab has always browsed with an empty query; the picker used to say so
  // with an `activeKey === 'local'` hardcode. Declaring it here changes nothing
  // about the behaviour and lets a second browsable source exist (LN-5).
  browseWithoutQuery: true,
  browseHint: 'Browse images by category or search by name.',

  getApiKey() {
    return '';
  },
  setApiKey() {},

  getSourceUrl(item) {
    const entry = item?._raw;
    return entry?._normalizedUrl || normalizeUrl(entry?.url);
  },

  normalizeUrl,

  basenameFromUrl,

  async findByUrl(rawUrl) {
    const normalizedUrl = normalizeUrl(rawUrl);
    await loadIndex();
    let entry = normalizedUrl ? urlAliasCache?.get(normalizedUrl) : null;
    if (!entry) {
      const targetBasename = basenameFromUrl(rawUrl).toLowerCase();
      if (targetBasename) {
        const matches = (sortedIndexCache || []).filter(
          (item) =>
            basenameFromUrl(item._normalizedUrl).toLowerCase() === targetBasename ||
            (item._variants || []).some(
              (variant) => basenameFromUrl(variant._normalizedUrl).toLowerCase() === targetBasename
            )
        );
        if (matches.length === 1) entry = matches[0];
      }
    }
    if (!entry) return null;
    return {
      id: `local-current-${entry.filename || normalizedUrl}`,
      thumb: thumbDisplayUrl(entry),
      title: entry.filename || basenameFromUrl(normalizedUrl) || 'Image',
      author: entry.extractedFrom || '',
      authorUrl: '',
      variantCount: entry._variantCount || 1,
      variants: entry._variants || [],
      dead: !!entry._dead,
      _raw: entry
    };
  },

  async search(query, page, _signal, { category = '', includeDead = false, keepUrl = '' } = {}) {
    await loadIndex();
    let filtered = sortedIndexCache || [];

    // Filter by category
    if (category) {
      filtered = filtered.filter(
        (e) => e.sectionCategory === category || e._sectionCategories?.has(category)
      );
    }

    // Filter by text against the precomputed per-entry haystack (built once in
    // buildNormalizedEntries) — identical substring semantics, no per-keystroke
    // re-lowercasing or variant iteration.
    if (query && query.trim()) {
      const q = query.trim().toLowerCase();
      filtered = filtered.filter((e) => (e._haystack || '').includes(q));
    }

    // MK-3: known-unrecoverable images leave the DEFAULT result set. The count
    // is reported rather than swallowed — a surface that silently drops records
    // is lying about what it holds — and `includeDead` brings them back for the
    // reveal. The exclusion happens BEFORE pagination on purpose: `total` drives
    // "Load More (60 of N)", so a total that counts records the pages will never
    // contain leaves the button permanently unfinished.
    //
    // `keepUrl` is exempt from the exclusion and is not decoration: it is the
    // image the user is REPLACING. Dropping it would make their own current
    // image vanish from the grid with no explanation, which is a worse failure
    // than the one the exclusion exists to prevent — so it is shown, disabled,
    // and told why. It is not counted as withheld, because it was not withheld.
    const keep = normalizeUrl(keepUrl);
    const deadCount = filtered.reduce(
      (n, e) => n + (e._dead && e._normalizedUrl !== keep ? 1 : 0),
      0
    );
    if (!includeDead && deadCount) {
      filtered = filtered.filter((e) => !e._dead || e._normalizedUrl === keep);
    }

    // Paginate
    const start = (page - 1) * PER_PAGE;
    const slice = filtered.slice(start, start + PER_PAGE);

    return {
      results: slice.map((e, i) => ({
        id: `local-${start + i}`,
        thumb: thumbDisplayUrl(e),
        title: e.filename || 'Image',
        author: e.extractedFrom || '',
        authorUrl: '',
        variantCount: e._variantCount || 1,
        variants: e._variants || [],
        dead: !!e._dead,
        _raw: e
      })),
      total: filtered.length,
      /** How many matches this query has that are known-unrecoverable. */
      deadCount,
      /** Of those, how many this call withheld. Zero when `includeDead`. */
      hiddenDeadCount: includeDead ? 0 : deadCount
    };
  },

  async download(item) {
    const entry = item._raw;
    const url = this.getSourceUrl(item);

    // A VENDORED BYTE WINS. Same origin as the builder's own assets, so this
    // also removes the CORS class of failure entirely for anything vendored —
    // and it is why a known-dead origin below is no longer fatal on its own.
    const vendoredKey = entry?._vendoredKey || '';
    if (vendoredKey) {
      try {
        const resp = await loadAsset(vendoredKey);
        const blob = await resp.blob();
        if (blob.size > 0) {
          return new File([blob], entry.filename || `image.${entry.format || 'jpg'}`, {
            type: blob.type || 'image/jpeg'
          });
        }
      } catch {
        // Expected-flow fallback: the bundle key is stale or the object is not
        // in R2 yet. Drop through to the origin rather than failing the pick.
      }
    }

    if (!url) {
      throw Object.assign(new Error('This image URL is invalid. Try a different image.'), {
        code: 'INVALID_URL'
      });
    }

    // MK-3. The sweep's list is exactly "dead origin AND unsatisfiable from this
    // entry's owning bundle", so this url cannot resolve from its pair — the fetch
    // below would spend 15s of timeout to learn what is already recorded. Note
    // the asymmetry the fail-soft contract forces: this refuses what is KNOWN
    // unavailable and never asserts that anything else is available.
    // Only now is a dead origin fatal: with no vendored copy there is nowhere
    // else to get these bytes, and the 15s fetch below would spend the timeout
    // learning what the sweep already recorded.
    if (isKnownDeadAssetUrl(url, entry?.sourceDomain)) {
      throw Object.assign(
        new Error(
          "This image's source is offline and no copy was archived. Try another image, or upload your own."
        ),
        { code: 'SOURCE_OFFLINE', sourceUrl: url }
      );
    }

    let origin = '';
    try {
      origin = new URL(url).origin;
    } catch {
      origin = '';
    }
    if (origin && corsDeniedOrigins.has(origin)) {
      throw Object.assign(
        new Error(
          'This host blocks browser downloads (CORS). Try a stock photo tab or upload your own.'
        ),
        { code: 'CORS', sourceUrl: url }
      );
    }

    try {
      const resp = await fetch(url, {
        mode: 'cors',
        signal: AbortSignal.timeout(DOWNLOAD_TIMEOUT_MS)
      });

      if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);

      const blob = await resp.blob();
      const mime = blob.type || 'image/jpeg';
      const filename = entry.filename || `image.${entry.format || 'jpg'}`;
      return new File([blob], filename, { type: mime });
    } catch (err) {
      // CORS or network failure — surface clear error
      if (err.name === 'TypeError' || err.message?.includes('CORS')) {
        if (origin) corsDeniedOrigins.add(origin);
        throw Object.assign(
          new Error(
            "This image couldn't be downloaded (CORS blocked). Try a stock photo tab or upload your own."
          ),
          { code: 'CORS', sourceUrl: url }
        );
      }
      throw err;
    }
  }
};
